import React from "react";
import styled from "styled-components";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiHome, FiSearch } from "react-icons/fi";
import { PageHeader } from "../../navigation/page-header";
import { Footer } from "../../navigation/footer/Footer";
import { primaryColor, white } from "../../shared/styles/color";
import { fontFamily } from "../../shared/styles/theme";

const Page = styled.div`
  font-family: ${fontFamily.font};
  background: #f9fafb;
  min-height: calc(100vh - 120px);
  text-align: left;
`;

const Shell = styled.section`
  max-width: 640px;
  margin: 0 auto;
  padding: 48px 24px 64px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Code = styled.div`
  font-size: clamp(4rem, 12vw, 6.5rem);
  font-weight: 800;
  line-height: 1;
  color: ${primaryColor};
  letter-spacing: -0.04em;
  margin-bottom: 8px;
`;

const Title = styled.h1`
  margin: 0 0 12px;
  font-size: clamp(1.5rem, 4vw, 2rem);
  color: #111827;
`;

const Lead = styled.p`
  margin: 0 0 8px;
  color: #4b5563;
  line-height: 1.6;
  font-size: 1.05rem;
`;

const PathHint = styled.p`
  margin: 0 0 28px;
  font-size: 0.9rem;
  color: #9ca3af;
  word-break: break-all;

  code {
    background: #f3f4f6;
    padding: 2px 8px;
    border-radius: 6px;
    color: #374151;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 32px;
`;

const PrimaryBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: ${primaryColor};
  color: ${white};
  border-radius: 10px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;

  &:hover {
    opacity: 0.92;
  }
`;

const GhostBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: ${white};
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }
`;

const Links = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  width: 100%;

  a {
    color: ${primaryColor};
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export default function NotFoundPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname || "/";

  return (
    <Page>
      <PageHeader variant="homepage" title="Page not found" />
      <Shell>
        <Code>404</Code>
        <Title>Page not found</Title>
        <Lead>
          The page you’re looking for doesn’t exist, was moved, or you may not have permission to view it.
        </Lead>
        <PathHint>
          Requested path: <code>{path}</code>
        </PathHint>

        <Actions>
          <PrimaryBtn to="/">
            <FiHome size={18} />
            Go to homepage
          </PrimaryBtn>
          <GhostBtn type="button" onClick={() => navigate(-1)}>
            <FiArrowLeft size={18} />
            Go back
          </GhostBtn>
        </Actions>

        <Links>
          <span style={{ color: "#6b7280", fontSize: "0.9rem" }}>Popular destinations</span>
          <Link to="/browse-projects">
            <FiSearch size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
            Browse projects
          </Link>
          <Link to="/browse-freelancers">Browse freelancers</Link>
          <Link to="/login">Log in</Link>
        </Links>
      </Shell>
      <Footer />
    </Page>
  );
}
